import { type Batch } from './Batch';
import type ApplicationModel from './ApplicationModel';

type Options = {
  // Batch will get executed within this timeout in milliseconds
  timeout?: number;
  // Batch will get executed if it reaches this number of queries
  limit?: number;
  // For mutation batches, whether to set the __reactivelyUpdate argument
  reactivelyUpdate?: boolean;
};

/**
 * Usage:
 *
 * ```js
 * const batcher = new Batcher({timeout: 1000});
 * const [f1, r1] = await Promise.all([
 *   batcher.find(Feature.select('id', 'name'), 'ABC-123'),
 *   batcher.find(Requirement.select('id', 'name', 'ABC-123-1')),
 *   batcher.all(Epic.select('id', 'name').where({projectId: currentProject.id}))
 * ]);
 * ```
 */
export default abstract class Batcher {
  _currentBatch: Batch;
  _timeout: number;
  _options: Options;

  constructor(options: Options = {}) {
    this._currentBatch = null;
    this._timeout = null;
    this._options = options;
  }

  abstract createBatch(): Batch;

  /**
   * @hidden
   *
   * Get the currently active batch, creating and queing one up if there is not
   * one
   */
  get currentBatch() {
    if (!this._currentBatch) {
      this._currentBatch = this.createBatch();

      if (this._options.timeout) {
        setTimeout(() => this.executeBatch(), this._options.timeout);
      }
    }
    return this._currentBatch;
  }

  /**
   * @hidden
   *
   * Execute the batch immediately if it has reached the query limit
   */
  checkLimit() {
    if (
      this._options.limit &&
      this._options.limit > 0 &&
      this._options.limit <= this.currentBatch.length
    ) {
      this.executeBatch();
    }
  }

  /**
   * @hidden
   *
   * Executes the batch and sets the current batch to null so a new one will be
   * created on the next call
   */
  async executeBatch(): Promise<Array<ApplicationModel | ApplicationModel[]>> {
    const batch = this._currentBatch;
    this._currentBatch = null;

    // There might not be a batch
    if (batch) return batch.execute();
  }

  /**
   * Manually execute the batch. Optionally provide a callback to add the
   * finders so that the whole batch operation can be wrapped up in this one
   * call.
   *
   * @example
   *   const b = new Batcher();
   *   const [feature, requirements] = await b.execute(() => {
   *     b.find(Feature.select('id'), 'FEAT-123'),
   *     b.all(Requirement.select('id').where({featureId: 'FEAT-123'}))
   *   });
   */
  async execute(
    batcherUpdate?: (batcher: this) => void
  ): Promise<Array<ApplicationModel | ApplicationModel[]>> {
    if (batcherUpdate) batcherUpdate(this);
    return this.executeBatch() || [];
  }
}
